import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
} from "@@/shared/date-time/iso-and-unix";
import { translatableZodSchema } from "@@/shared/translations/translation";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderChangeZodSchema } from "./order-changes/_order-change";
import { isOrderItemBookableTimeOrderChange } from "./order-changes/order-item-bookable-time.order-change";

const orderChangedEventZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("ORDER_CHANGED"),
        reason: translatableZodSchema,
        changes: z.array(orderChangeZodSchema).min(1),
        orderSlug: z.string().min(1),
        orderGroupReference: z.string().min(1),
        fulfillmentDate: isoAndUnixZodSchema,
    }),
);
type OrderChangedEvent = z.infer<typeof orderChangedEventZodObject>;
const orderChangedEventZodSchema =
    orderChangedEventZodObject as z.ZodSchema<OrderChangedEvent>;

type FactoryParams = Except<
    OrderChangedEvent,
    "_id" | "_createdAt" | "_type" | "type" | "signature" | "fulfillmentDate"
> & {
    /**
     * The fulfillment date at the time of the event
     * This is used as a fallback if the fulfillment date
     * is not changed by the event
     * @type {IsoAndUnixTimestamp}
     */
    currentFulfillmentDate: IsoAndUnixTimestamp;
};

const orderChangeFulfillmentDateUpdate = (
    changes: FactoryParams["changes"],
): IsoAndUnixTimestamp | undefined => {
    // Check if any of the changes are bookable time changed
    const fulfillmentDataChangedTo = changes.filter(
        isOrderItemBookableTimeOrderChange,
    )?.[0]?.data.to;

    if (fulfillmentDataChangedTo) return fulfillmentDataChangedTo?.start;
    return undefined;
};

const orderChangedEventFactory = (params: FactoryParams): OrderChangedEvent => {
    const fulfillmentDate =
        orderChangeFulfillmentDateUpdate(params.changes) ??
        params.currentFulfillmentDate;
    const changedEvent: OrderChangedEvent = {
        type: "ORDER_CHANGED",
        ...params,
        ..._orderEventBaseFactory({
            ...params,
            signature: signatureFactory(params.orderId, "ORDER_CHANGED", [
                params._version.toString(),
            ]),
        }),
        fulfillmentDate,
    };
    return changedEvent;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOrderChangedEvent = (event: any): event is OrderChangedEvent => {
    return (
        !!event &&
        event._type === "ORDER_EVENT" &&
        event.type === "ORDER_CHANGED"
    );
};

export {
    isOrderChangedEvent,
    orderChangedEventFactory,
    orderChangedEventZodObject,
    orderChangedEventZodSchema,
};
export type { OrderChangedEvent };
